import React, { useState, useEffect } from 'react';
import { supabase } from '../../api/supabase';
import {
  colors,
  spacing,
  typography,
  radius,
  transitions,
  components
} from '../../styles/monochromeTheme';
import {
  PlusCircle,
  Trash2,
  Copy,
  Check,
  X,
  Calendar,
  Plane,
  Users,
  Ship
} from 'lucide-react';

const ScheduleGeorges = () => {
  const [schedules, setSchedules] = useState([]);
  const [yachts, setYachts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch yachts from Supabase
  useEffect(() => {
    fetchYachts();
  }, []);

  // Keyboard shortcuts for efficiency
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + N to add new schedule
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        addSchedule();
      }
      // Ctrl/Cmd + C to copy all
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && e.shiftKey && schedules.length > 0) {
        e.preventDefault();
        copyToClipboard();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [schedules]);

  const fetchYachts = async () => {
    try {
      const { data, error } = await supabase
        .from('yachts')
        .select('name')
        .order('name');

      if (error) throw error;
      setYachts(data || []);
    } catch (error) {
      console.error('Error fetching yachts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if route is departure or landing
  const getFlightType = (route) => {
    // SBH/SXM = leaving Saint Barth = DEPARTURE
    // SXM/SBH = arriving to Saint Barth = LANDING
    if (route.startsWith('SBH/') || route.startsWith('AXA/')) {
      return 'DEPARTURE';
    }
    return 'LANDING';
  };

  // Add new schedule entry
  const addSchedule = () => {
    const route = 'SXM/SBH';
    const newSchedule = {
      id: Date.now(),
      type: 'VIP SERVICE',
      route: route,
      customInfo: '',
      passengers: [
        {
          id: Date.now(),
          count: '',
          name: '',
          flights: [
            {
              id: Date.now() + 1,
              flightNumber: '',
              time: '',
              arrivalDeparture: getFlightType(route)
            }
          ]
        }
      ],
      location: 'MFL',
      yacht: ''
    };
    setSchedules([...schedules, newSchedule]);
  };

  // Remove schedule
  const removeSchedule = (scheduleId) => {
    setSchedules(schedules.filter(s => s.id !== scheduleId));
  };

  // Update schedule field
  const updateSchedule = (scheduleId, field, value) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        const updated = { ...s, [field]: value };
        // Update flight types when route changes
        if (field === 'route') {
          const flightType = getFlightType(value);
          updated.passengers = s.passengers.map(p => ({
            ...p,
            flights: p.flights.map(f => ({
              ...f,
              arrivalDeparture: flightType
            }))
          }));
        }
        return updated;
      }
      return s;
    }));
  };

  // Add passenger to schedule
  const addPassenger = (scheduleId) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: [
            ...s.passengers,
            {
              id: Date.now(),
              count: '',
              name: '',
              flights: [
                {
                  id: Date.now() + 1,
                  flightNumber: '',
                  time: '',
                  arrivalDeparture: getFlightType(s.route)
                }
              ]
            }
          ]
        };
      }
      return s;
    }));
  };

  // Remove passenger
  const removePassenger = (scheduleId, passengerId) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: s.passengers.filter(p => p.id !== passengerId)
        };
      }
      return s;
    }));
  };

  // Update passenger
  const updatePassenger = (scheduleId, passengerId, field, value) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: s.passengers.map(p =>
            p.id === passengerId ? { ...p, [field]: value } : p
          )
        };
      }
      return s;
    }));
  };

  // Add flight to passenger
  const addFlight = (scheduleId, passengerId) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: s.passengers.map(p => {
            if (p.id === passengerId) {
              return {
                ...p,
                flights: [
                  ...p.flights,
                  {
                    id: Date.now(),
                    flightNumber: '',
                    time: '',
                    arrivalDeparture: getFlightType(s.route)
                  }
                ]
              };
            }
            return p;
          })
        };
      }
      return s;
    }));
  };

  // Remove flight
  const removeFlight = (scheduleId, passengerId, flightId) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: s.passengers.map(p => {
            if (p.id === passengerId) {
              return {
                ...p,
                flights: p.flights.filter(f => f.id !== flightId)
              };
            }
            return p;
          })
        };
      }
      return s;
    }));
  };

  // Update flight
  const updateFlight = (scheduleId, passengerId, flightId, field, value) => {
    setSchedules(schedules.map(s => {
      if (s.id === scheduleId) {
        return {
          ...s,
          passengers: s.passengers.map(p => {
            if (p.id === passengerId) {
              return {
                ...p,
                flights: p.flights.map(f =>
                  f.id === flightId ? { ...f, [field]: value } : f
                )
              };
            }
            return p;
          })
        };
      }
      return s;
    }));
  };

  // Generate formatted text for copy/paste
  const generateScheduleText = () => {
    return schedules.map(schedule => {
      let text = `* ${schedule.type}\n`;
      text += `${schedule.route}\n`;

      // Add custom info if present
      if (schedule.customInfo && schedule.customInfo.trim()) {
        text += `${schedule.customInfo}\n`;
      }

      schedule.passengers.forEach(passenger => {
        text += `${passenger.count} PAX - ${passenger.name}\n`;

        passenger.flights.forEach(flight => {
          text += `${flight.arrivalDeparture} @ ${flight.time}`;
          if (flight.flightNumber) {
            text += ` on ${flight.flightNumber}`;
          }
          text += '\n';
        });
      });

      text += `@ ${schedule.location}\n`;
      text += `${schedule.yacht}\n`;

      return text;
    }).join('\n');
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const text = generateScheduleText();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div style={components.loadingContainer}>
        <div>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #eee',
            borderTop: '3px solid #000',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <p style={components.loadingText}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bgPrimary,
      padding: spacing.xl
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Improved Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.xl,
          paddingBottom: spacing.lg,
          borderBottom: `2px solid ${colors.borderLight}`
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <Calendar size={28} style={{ color: colors.primary }} />
              <h1 style={{
                margin: 0,
                fontSize: typography.fontSize.xxl,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary
              }}>
                Schedule Georges
              </h1>
              <span style={{
                fontSize: typography.fontSize.sm,
                color: colors.textTertiary,
                marginLeft: spacing.sm
              }}>
                - Transferts VIP
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center' }}>
            {schedules.length > 0 && (
              <div style={{
                padding: `${spacing.xs} ${spacing.md}`,
                background: colors.bgSecondary,
                borderRadius: radius.lg,
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                fontWeight: typography.fontWeight.semibold
              }}>
                {schedules.length} transfert{schedules.length > 1 ? 's' : ''}
              </div>
            )}
            <button
              onClick={addSchedule}
              style={{
                ...components.primaryBtn,
                padding: `${spacing.sm} ${spacing.lg}`,
                fontSize: typography.fontSize.md,
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm
              }}
              title="Ajouter un nouveau transfert (Ctrl/⌘ + N)"
            >
              <PlusCircle size={16} />
              Nouveau
            </button>
            {schedules.length > 0 && (
              <button
                onClick={copyToClipboard}
                style={{
                  ...components.secondaryBtn,
                  padding: `${spacing.sm} ${spacing.lg}`,
                  fontSize: typography.fontSize.md,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm
                }}
                title="Copier tous les transferts (Ctrl/⌘ + Shift + C)"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copié' : 'Copier'}
              </button>
            )}
          </div>
        </div>

        {/* Schedules List */}
        {schedules.length === 0 ? (
          <div style={{
            ...components.card,
            padding: spacing.xxl,
            textAlign: 'center'
          }}>
            <Calendar size={48} style={{ color: colors.textTertiary, marginBottom: spacing.lg }} />
            <h3 style={{
              margin: 0,
              fontSize: typography.fontSize.lg,
              color: colors.textSecondary,
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.xs
            }}>
              Aucun transfert planifié
            </h3>
            <p style={{
              margin: 0,
              color: colors.textTertiary,
              fontSize: typography.fontSize.sm
            }}>
              Cliquez sur "Nouveau" pour commencer
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: spacing.md
          }}>
            {schedules.map((schedule, scheduleIndex) => (
              <div key={schedule.id} style={{
                background: colors.bgSecondary,
                borderRadius: radius.lg,
                padding: spacing.md,
                border: `1px solid ${colors.borderLight}`,
                position: 'relative',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                {/* Schedule Header - Compact */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: spacing.sm
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs
                  }}>
                    <span style={{
                      padding: `2px ${spacing.sm}`,
                      background: colors.primary,
                      color: colors.bgPrimary,
                      borderRadius: radius.sm,
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      letterSpacing: typography.letterSpacing.wide
                    }}>
                      #{scheduleIndex + 1}
                    </span>
                    <Ship size={14} style={{ color: colors.textTertiary }} />
                  </div>

                  <button
                    onClick={() => removeSchedule(schedule.id)}
                    style={{
                      background: colors.accentErrorBg,
                      border: 'none',
                      borderRadius: radius.sm,
                      padding: spacing.xs,
                      cursor: 'pointer',
                      color: colors.accentError,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: transitions.normal
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.accentError;
                      e.currentTarget.style.color = colors.bgPrimary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = colors.accentErrorBg;
                      e.currentTarget.style.color = colors.accentError;
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>

                {/* Main Info - Stacked */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.xs,
                  marginBottom: spacing.sm
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.xs }}>
                    <select
                      value={schedule.route}
                      onChange={(e) => updateSchedule(schedule.id, 'route', e.target.value)}
                      style={{
                        padding: spacing.xs,
                        fontSize: typography.fontSize.sm,
                        border: `2px solid ${colors.border}`,
                        borderRadius: radius.sm,
                        background: colors.bgPrimary,
                        color: colors.textPrimary,
                        fontWeight: typography.fontWeight.bold,
                        cursor: 'pointer',
                        transition: transitions.fast
                      }}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = colors.border}
                    >
                      <option value="SXM/SBH">SXM/SBH</option>
                      <option value="SBH/SXM">SBH/SXM</option>
                      <option value="SXM/AXA">SXM/AXA</option>
                      <option value="AXA/SXM">AXA/SXM</option>
                    </select>

                    <select
                      value={schedule.location}
                      onChange={(e) => updateSchedule(schedule.id, 'location', e.target.value)}
                      style={{
                        padding: spacing.xs,
                        fontSize: typography.fontSize.sm,
                        border: `2px solid ${colors.border}`,
                        borderRadius: radius.sm,
                        background: colors.bgPrimary,
                        color: colors.textPrimary,
                        fontWeight: typography.fontWeight.semibold,
                        cursor: 'pointer',
                        transition: transitions.fast
                      }}
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = colors.border}
                    >
                      <option value="MFL">MFL</option>
                      <option value="QDR">QDR</option>
                      <option value="AUTRE">AUTRE</option>
                    </select>
                  </div>

                  <input
                    type="text"
                    value={schedule.type}
                    onChange={(e) => updateSchedule(schedule.id, 'type', e.target.value)}
                    style={{
                      width: '100%',
                      padding: spacing.xs,
                      fontSize: typography.fontSize.sm,
                      border: `2px solid ${colors.border}`,
                      borderRadius: radius.sm,
                      background: colors.bgPrimary,
                      color: colors.textPrimary,
                      transition: transitions.fast
                    }}
                    placeholder="Type (VIP SERVICE)"
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />

                  <select
                    value={schedule.yacht}
                    onChange={(e) => updateSchedule(schedule.id, 'yacht', e.target.value)}
                    style={{
                      width: '100%',
                      padding: spacing.xs,
                      fontSize: typography.fontSize.sm,
                      border: `2px solid ${colors.border}`,
                      borderRadius: radius.sm,
                      background: colors.bgPrimary,
                      color: colors.textPrimary,
                      cursor: 'pointer',
                      transition: transitions.fast
                    }}
                    onFocus={(e) => e.target.style.borderColor = colors.primary}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  >
                    <option value="">Yacht...</option>
                    {yachts.map(yacht => (
                      <option key={yacht.name} value={yacht.name}>
                        {yacht.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Info - Compact */}
                {schedule.customInfo !== undefined && (
                  <div style={{ marginBottom: spacing.sm }}>
                    <input
                      type="text"
                      value={schedule.customInfo}
                      onChange={(e) => updateSchedule(schedule.id, 'customInfo', e.target.value)}
                      style={{
                        width: '100%',
                        padding: spacing.xs,
                        fontSize: typography.fontSize.xs,
                        border: `2px solid ${colors.border}`,
                        borderRadius: radius.sm,
                        background: colors.bgPrimary,
                        color: colors.textSecondary,
                        fontStyle: 'italic',
                        transition: transitions.fast
                      }}
                      placeholder="Note..."
                      onFocus={(e) => e.target.style.borderColor = colors.primary}
                      onBlur={(e) => e.target.style.borderColor = colors.border}
                    />
                  </div>
                )}

                {/* Passengers Section */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    marginBottom: spacing.sm,
                    paddingTop: spacing.sm,
                    borderTop: `1px solid ${colors.borderLight}`
                  }}>
                    <Users size={14} style={{ color: colors.textTertiary }} />
                    <span style={{
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textTertiary,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wide
                    }}>
                      Passagers
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.sm
                  }}>
                    {schedule.passengers.map((passenger) => (
                      <div key={passenger.id} style={{
                        background: colors.bgPrimary,
                        padding: spacing.sm,
                        borderRadius: radius.md,
                        border: `1px solid ${colors.border}`
                      }}>
                        {/* Passenger Row - Compact */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '60px 1fr auto',
                          gap: spacing.xs,
                          alignItems: 'center',
                          marginBottom: spacing.xs
                        }}>
                          <input
                            type="text"
                            value={passenger.count}
                            onChange={(e) => updatePassenger(schedule.id, passenger.id, 'count', e.target.value)}
                            style={{
                              padding: spacing.xs,
                              fontSize: typography.fontSize.sm,
                              border: `2px solid ${colors.border}`,
                              borderRadius: radius.sm,
                              background: colors.bgSecondary,
                              color: colors.textPrimary,
                              fontWeight: typography.fontWeight.bold,
                              textAlign: 'center',
                              transition: transitions.fast
                            }}
                            placeholder="PAX"
                            onFocus={(e) => e.target.style.borderColor = colors.primary}
                            onBlur={(e) => e.target.style.borderColor = colors.border}
                          />

                          <input
                            type="text"
                            value={passenger.name}
                            onChange={(e) => updatePassenger(schedule.id, passenger.id, 'name', e.target.value)}
                            style={{
                              padding: spacing.xs,
                              fontSize: typography.fontSize.sm,
                              border: `2px solid ${colors.border}`,
                              borderRadius: radius.sm,
                              background: colors.bgSecondary,
                              color: colors.textPrimary,
                              transition: transitions.fast
                            }}
                            placeholder="Nom"
                            onFocus={(e) => e.target.style.borderColor = colors.primary}
                            onBlur={(e) => e.target.style.borderColor = colors.border}
                          />

                          <div style={{ display: 'flex', gap: spacing.xs }}>
                            <button
                              onClick={() => addFlight(schedule.id, passenger.id)}
                              style={{
                                padding: spacing.xs,
                                fontSize: typography.fontSize.xs,
                                border: `2px solid ${colors.border}`,
                                borderRadius: radius.sm,
                                background: colors.bgSecondary,
                                color: colors.textPrimary,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                fontWeight: typography.fontWeight.semibold,
                                transition: transitions.fast
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = colors.primary;
                                e.currentTarget.style.color = colors.bgPrimary;
                                e.currentTarget.style.borderColor = colors.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = colors.bgSecondary;
                                e.currentTarget.style.color = colors.textPrimary;
                                e.currentTarget.style.borderColor = colors.border;
                              }}
                            >
                              <Plane size={12} />
                              Vol
                            </button>
                            {schedule.passengers.length > 1 && (
                              <button
                                onClick={() => removePassenger(schedule.id, passenger.id)}
                                style={{
                                  padding: spacing.xs,
                                  border: 'none',
                                  borderRadius: radius.sm,
                                  background: colors.accentErrorBg,
                                  color: colors.accentError,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  transition: transitions.fast
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = colors.accentError;
                                  e.currentTarget.style.color = colors.bgPrimary;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = colors.accentErrorBg;
                                  e.currentTarget.style.color = colors.accentError;
                                }}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Flights - Compact */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {passenger.flights.map((flight) => (
                            <div key={flight.id} style={{
                              display: 'grid',
                              gridTemplateColumns: '95px 80px 1fr auto',
                              gap: spacing.xs,
                              alignItems: 'center'
                            }}>
                              <span style={{
                                padding: `2px ${spacing.xs}`,
                                fontSize: typography.fontSize.xs,
                                fontWeight: typography.fontWeight.bold,
                                color: flight.arrivalDeparture === 'DEPARTURE' ? colors.accentWarning : colors.accentSuccess,
                                background: flight.arrivalDeparture === 'DEPARTURE' ? colors.accentWarningBg : colors.accentSuccessBg,
                                borderRadius: radius.sm,
                                textAlign: 'center',
                                letterSpacing: typography.letterSpacing.wide
                              }}>
                                {flight.arrivalDeparture}
                              </span>

                              <input
                                type="text"
                                value={flight.time}
                                onChange={(e) => updateFlight(schedule.id, passenger.id, flight.id, 'time', e.target.value)}
                                style={{
                                  padding: `2px ${spacing.xs}`,
                                  fontSize: typography.fontSize.sm,
                                  border: `2px solid ${colors.border}`,
                                  borderRadius: radius.sm,
                                  background: colors.bgSecondary,
                                  color: colors.textPrimary,
                                  fontWeight: typography.fontWeight.semibold,
                                  transition: transitions.fast,
                                  textAlign: 'center'
                                }}
                                placeholder="HH:MM"
                                onFocus={(e) => e.target.style.borderColor = colors.primary}
                                onBlur={(e) => e.target.style.borderColor = colors.border}
                              />

                              <input
                                type="text"
                                value={flight.flightNumber}
                                onChange={(e) => updateFlight(schedule.id, passenger.id, flight.id, 'flightNumber', e.target.value)}
                                style={{
                                  padding: `2px ${spacing.xs}`,
                                  fontSize: typography.fontSize.sm,
                                  border: `2px solid ${colors.border}`,
                                  borderRadius: radius.sm,
                                  background: colors.bgSecondary,
                                  color: colors.textPrimary,
                                  transition: transitions.fast
                                }}
                                placeholder="N° vol"
                                onFocus={(e) => e.target.style.borderColor = colors.primary}
                                onBlur={(e) => e.target.style.borderColor = colors.border}
                              />

                              {passenger.flights.length > 1 && (
                                <button
                                  onClick={() => removeFlight(schedule.id, passenger.id, flight.id)}
                                  style={{
                                    padding: '2px',
                                    border: 'none',
                                    borderRadius: radius.sm,
                                    background: colors.accentErrorBg,
                                    color: colors.accentError,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: transitions.fast
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = colors.accentError;
                                    e.currentTarget.style.color = colors.bgPrimary;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = colors.accentErrorBg;
                                    e.currentTarget.style.color = colors.accentError;
                                  }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Add Passenger Button */}
                    <button
                      onClick={() => addPassenger(schedule.id)}
                      style={{
                        padding: spacing.sm,
                        fontSize: typography.fontSize.sm,
                        border: `2px dashed ${colors.border}`,
                        borderRadius: radius.lg,
                        background: 'transparent',
                        color: colors.textSecondary,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: spacing.xs,
                        fontWeight: typography.fontWeight.semibold,
                        transition: transitions.fast
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = colors.primary;
                        e.currentTarget.style.color = colors.bgPrimary;
                        e.currentTarget.style.borderColor = colors.primary;
                        e.currentTarget.style.borderStyle = 'solid';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = colors.textSecondary;
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.borderStyle = 'dashed';
                      }}
                    >
                      <PlusCircle size={16} />
                      Ajouter un passager
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleGeorges;
